import React, { type FC } from 'react';
import {
  formatSchedule,
  isJobCustomised,
  routineNameWithSchedule,
  routineUsesClockPhrase,
  sameSchedule,
  scheduleOptionsFor,
  splitRoutineName,
  type RoutineSchedule,
} from './mondayIntegrationLogic';
import type {
  IntegrationJobEntry,
  IntegrationRoutine,
} from './integrationPanelTypes';
import { JobIcon, RotateIcon } from './integrationPanelIcons';
import {
  providerClass,
  type IntegrationProviderConfig,
} from './integrationProvider';

interface IntegrationJobCardProps {
  entry: IntegrationJobEntry;
  index: number;
  provider: IntegrationProviderConfig;
  jobOn: boolean;
  routineState: Readonly<Record<string, boolean>>;
  openSchedule: string | null;
  onToggleJob: () => void;
  onToggleRoutine: (routine: IntegrationRoutine) => void;
  onToggleSchedule: (routineId: string) => void;
  onPatchSchedule: (
    routine: IntegrationRoutine,
    schedule: RoutineSchedule,
  ) => void;
  onEditJob: (job: IntegrationJobEntry['job']) => void;
  onRevertJob: (job: IntegrationJobEntry['job']) => void;
}

const renderRoutineName = (
  name: string,
  schedule: RoutineSchedule,
  onScheduleClick: () => void,
  provider: IntegrationProviderConfig,
) => {
  const parts = splitRoutineName(name);
  const scheduleButton = (
    <button
      type='button'
      className={providerClass(provider, 'schedule-button')}
      onClick={onScheduleClick}
    >
      {formatSchedule(schedule, parts.precededByAt)}
    </button>
  );
  if (parts.after === undefined) {
    return (
      <>
        {name}{' '}
        <span className={providerClass(provider, 'schedule-parameter')}>
          {scheduleButton}
        </span>
      </>
    );
  }
  return (
    <>
      {parts.before}
      {scheduleButton}
      {parts.after}
    </>
  );
};

export const IntegrationJobCard: FC<IntegrationJobCardProps> = ({
  entry,
  index,
  provider,
  jobOn,
  routineState,
  openSchedule,
  onToggleJob,
  onToggleRoutine,
  onToggleSchedule,
  onPatchSchedule,
  onEditJob,
  onRevertJob,
}) => {
  const rewritten = entry.job.activeGeneration > 1;
  const customised = isJobCustomised(entry.job, entry.routines);
  const jobToggleClass = `${providerClass(provider, 'toggle-switch')}${
    jobOn ? ' is-on' : ''
  }`;
  return (
    <article
      className={`${providerClass(provider, 'job-card')}${jobOn ? ' is-on' : ''}`}
      key={entry.job.id}
    >
      <div className={providerClass(provider, 'job-head')}>
        <span className={providerClass(provider, 'job-icon')}>
          <JobIcon index={index} />
        </span>
        <div className={providerClass(provider, 'job-copy')}>
          <h4>{entry.job.title}</h4>
          <p>{entry.job.scope}</p>
        </div>
        <div className={providerClass(provider, 'job-actions')}>
          {customised && (
            <span className={providerClass(provider, 'customised')}>
              Customised
            </span>
          )}
          {rewritten && (
            <button
              type='button'
              className={providerClass(provider, 'icon-button')}
              aria-label='Revert to their original version'
              title='Revert to their original version'
              onClick={() => onRevertJob(entry.job)}
            >
              <RotateIcon />
            </button>
          )}
          <button
            type='button'
            className={providerClass(provider, 'edit-button')}
            onClick={() => onEditJob(entry.job)}
          >
            Edit the job
          </button>
          <button
            type='button'
            className={jobToggleClass}
            role='switch'
            aria-checked={jobOn}
            aria-label={entry.job.title}
            onClick={onToggleJob}
          >
            <span className={providerClass(provider, 'toggle-knob')} />
          </button>
        </div>
      </div>
      <div className={providerClass(provider, 'job-body')}>
        {entry.routines.map((routine) => {
          const active = routineState[routine.id] ?? routine.active;
          const routineToggleClass = `${providerClass(
            provider,
            'toggle-switch',
          )}${active ? ' is-on' : ''}`;
          const scheduleUsesClockPhrase = routineUsesClockPhrase(routine.name);
          const options = scheduleOptionsFor(routine, scheduleUsesClockPhrase);
          return (
            <div
              className={`${providerClass(provider, 'routine-row')}${active ? ' is-on' : ''}`}
              key={routine.id}
            >
              <div className={providerClass(provider, 'routine-sentence')}>
                {renderRoutineName(
                  routine.name,
                  routine.schedule,
                  () => onToggleSchedule(routine.id),
                  provider,
                )}
                {openSchedule === routine.id && (
                  <div
                    className={providerClass(provider, 'schedule-menu')}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    {options.map((option) => (
                      <button
                        type='button'
                        key={`${option.cadence}-${option.timeOfDay}-${option.dayOfWeek ?? ''}-${option.dayOfMonth ?? ''}`}
                        aria-selected={sameSchedule(option, routine.schedule)}
                        onClick={() => onPatchSchedule(routine, option)}
                      >
                        {formatSchedule(option, scheduleUsesClockPhrase)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type='button'
                className={routineToggleClass}
                role='switch'
                aria-checked={active}
                aria-label={routineNameWithSchedule(
                  routine.name,
                  routine.schedule,
                )}
                onClick={() => onToggleRoutine(routine)}
              >
                <span className={providerClass(provider, 'toggle-knob')} />
              </button>
            </div>
          );
        })}
      </div>
    </article>
  );
};
